import { Button } from '@hakimo-ui/shared/ui-base';
import { useEffect, useState } from 'react';
import { isRedacted } from './redaction';

type CopyState = 'idle' | 'copied' | 'failed';

const LABEL: Record<CopyState, string> = {
  idle: 'Copy',
  copied: 'Copied',
  failed: 'Copy failed',
};

/**
 * The row as it stood before the change. Not a revert payload: credentials
 * are redacted, so applying it would write `***` over the real values.
 */
export function PriorState({ config }: { config: Record<string, unknown> }) {
  const [state, setState] = useState<CopyState>('idle');
  const json = JSON.stringify(config, null, 2);
  const redacted = isRedacted(config);

  useEffect(() => {
    if (state === 'idle') {
      return;
    }
    const timer = setTimeout(() => setState('idle'), 2500);
    return () => clearTimeout(timer);
  }, [state]);

  // Never report success on a rejected write.
  const onCopy = () =>
    navigator.clipboard
      .writeText(json)
      .then(() => setState('copied'))
      .catch(() => setState('failed'));

  return (
    <div className="mt-3 border-t border-gray-200 pt-3 dark:border-gray-800">
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500">State before the change:</span>
        <Button variant="link" onClick={onCopy}>
          {LABEL[state]}
        </Button>
        {state === 'failed' && (
          <span className="text-xs text-red-600 dark:text-red-400">
            select the text below instead
          </span>
        )}
      </div>
      {redacted && (
        <p className="mt-1 rounded bg-amber-50 p-2 text-xs text-amber-800 dark:bg-amber-950 dark:text-amber-300">
          Credentials in this document were redacted to <code>***</code> before
          it was stored. Do not apply it as-is — that would overwrite the real
          values. Restore the redacted fields from their own source.
        </p>
      )}
      <pre className="mt-1 max-h-64 overflow-auto rounded bg-white p-2 font-mono text-xs dark:bg-black">
        {json}
      </pre>
    </div>
  );
}
